import {
    Color,
    ColorRepresentation,
    FrontSide,
    Material,
    Mesh,
    MeshBasicMaterial,
    SRGBColorSpace,
    Texture,
} from 'three'
import {
    chameleonFresnelPower,
    chameleonFresnelRimEnd,
    chameleonFresnelRimStart,
} from '../../config/constants'
import type { OutlineHandle } from './outline'


// ─── Konstanten ───────────────────────────────────────────────────────────────
export const SKIN_COLOR_LERP_SPEED = 10
export const COLOR_SATURATION_MULTIPLIER = 0.7
const _hslScratch = { h: 0, s: 0, l: 0 }


// ─── Typen ────────────────────────────────────────────────────────────────────
export type CharacterSkinMaterial = Material & {
    color: Color
    map: Texture | null
    needsUpdate: boolean
}

export type CharacterFresnelMaterial = MeshBasicMaterial & {
    userData: MeshBasicMaterial['userData'] & {
        fresnelTintColor?: Color
        fresnelShader?: {
            uniforms: {
                fresnelTintColor: { value: Color }
                fresnelPower: { value: number }
                fresnelRimStart: { value: number }
                fresnelRimEnd: { value: number }
            }
        }
    }
}


// ─── Type Guards ──────────────────────────────────────────────────────────────
export function isTexturableColorMaterial(value: unknown): value is CharacterSkinMaterial {
    if (!(value instanceof Material)) return false
    return 'color' in value && value.color instanceof Color && 'map' in value
}


// ─── Material-Auflösung ───────────────────────────────────────────────────────
const PREFERRED_SKIN_NAMES = ['Chameleon', 'Chameleon_01'] as const

export function resolveCharacterSkinMaterial(
    materials: Record<string, unknown>,
): CharacterSkinMaterial | null {
    for (const name of PREFERRED_SKIN_NAMES) {
        const candidate = materials[name]
        if (isTexturableColorMaterial(candidate)) return candidate
    }
    const fallback = Object.values(materials).find(isTexturableColorMaterial)
    return fallback ?? null
}

export function resolveCharacterSkinMaterials(
    materials: Record<string, unknown>,
): CharacterSkinMaterial[] {
    const resolved = new Set<CharacterSkinMaterial>()
    for (const name of PREFERRED_SKIN_NAMES) {
        const candidate = materials[name]
        if (isTexturableColorMaterial(candidate)) resolved.add(candidate)
    }
    const primary = resolveCharacterSkinMaterial(materials)
    if (primary) resolved.add(primary)
    return Array.from(resolved)
}

export function resolveCharacterBasicMaterialTargets(
    materials: Record<string, unknown>,
): CharacterSkinMaterial[] {
    const materialGroups = [
        ['Chameleon', 'Chameleon_01'],
        ['Eyes', 'Chameleon_Eyes'],
        ['Tongue', 'Chameleon_Tongue'],
        ['M_ShoeBase', 'M_ShoeBase.001'],
        ['M_ShoeColor', 'M_ShoeColor.001'],
    ]
    const resolved = new Set<CharacterSkinMaterial>()
    for (const group of materialGroups) {
        for (const name of group) {
            const candidate = materials[name]
            if (isTexturableColorMaterial(candidate)) resolved.add(candidate)
        }
    }
    return Array.from(resolved)
}


// ─── Fresnel-Material ─────────────────────────────────────────────────────────
export function createCharacterBasicMaterial(
    sourceMaterial: CharacterSkinMaterial,
): MeshBasicMaterial {
    const enableFresnel = sourceMaterial.name === 'Chameleon'
    const basicMaterial = new MeshBasicMaterial({
        color: enableFresnel ? '#ffffff' : sourceMaterial.color.clone(),
        map: sourceMaterial.map,
        toneMapped: false,
    }) as CharacterFresnelMaterial

    basicMaterial.name = sourceMaterial.name
    basicMaterial.side = FrontSide
    basicMaterial.transparent = sourceMaterial.transparent
    basicMaterial.alphaTest = 'alphaTest' in sourceMaterial ? sourceMaterial.alphaTest as number : 0
    basicMaterial.opacity = sourceMaterial.opacity
    basicMaterial.depthWrite = !sourceMaterial.transparent
    basicMaterial.depthTest = true

    if (basicMaterial.map) {
        basicMaterial.map.colorSpace = SRGBColorSpace
    }

    if (enableFresnel) {
        basicMaterial.userData.fresnelTintColor = sourceMaterial.color.clone()
        basicMaterial.onBeforeCompile = (shader) => {
            shader.uniforms.fresnelTintColor = {
                value: basicMaterial.userData.fresnelTintColor?.clone() ?? new Color('#ffffff'),
            }
            shader.uniforms.fresnelPower = { value: chameleonFresnelPower }
            shader.uniforms.fresnelRimStart = { value: chameleonFresnelRimStart }
            shader.uniforms.fresnelRimEnd = { value: chameleonFresnelRimEnd }

            basicMaterial.userData.fresnelShader = {
                uniforms: {
                    fresnelTintColor: shader.uniforms.fresnelTintColor as { value: Color },
                    fresnelPower: shader.uniforms.fresnelPower as { value: number },
                    fresnelRimStart: shader.uniforms.fresnelRimStart as { value: number },
                    fresnelRimEnd: shader.uniforms.fresnelRimEnd as { value: number },
                },
            }

            shader.vertexShader = shader.vertexShader
                .replace(
                    '#include <common>',
                    `#include <common>
                    varying vec3 vCharacterViewNormal;`,
                )
                .replace(
                    '#include <defaultnormal_vertex>',
                    `#include <defaultnormal_vertex>
                    vCharacterViewNormal = normalize(normalMatrix * transformedNormal);`,
                )

            shader.fragmentShader = shader.fragmentShader
                .replace(
                    '#include <common>',
                    `#include <common>
                    varying vec3 vCharacterViewNormal;
                    uniform vec3 fresnelTintColor;
                    uniform float fresnelPower;
                    uniform float fresnelRimStart;
                    uniform float fresnelRimEnd;`,
                )
                .replace(
                    '#include <map_fragment>',
                    `#include <map_fragment>
                    vec3 viewNormal = normalize(vCharacterViewNormal);
                    float fresnelMask = clamp(viewNormal.z, 0.0, 1.0);
                    fresnelMask = pow(fresnelMask, fresnelPower);
                    fresnelMask = smoothstep(fresnelRimStart, fresnelRimEnd, fresnelMask);
                    float tintMask = 1.0 - fresnelMask;
                    vec3 fresnelMultiplier = mix(vec3(1.0), fresnelTintColor, tintMask);
                    diffuseColor.rgb *= fresnelMultiplier;`,
                )
        }
    }
    return basicMaterial
}

export function setCharacterFresnelParams(
    material: MeshBasicMaterial,
    power: number,
    rimStart: number,
    rimEnd: number,
) {
    const fresnelMaterial = material as CharacterFresnelMaterial
    if (!fresnelMaterial.userData.fresnelShader) return
    fresnelMaterial.userData.fresnelShader.uniforms.fresnelPower.value = power
    fresnelMaterial.userData.fresnelShader.uniforms.fresnelRimStart.value = rimStart
    fresnelMaterial.userData.fresnelShader.uniforms.fresnelRimEnd.value = rimEnd
}

export function getCharacterFresnelTint(material: MeshBasicMaterial): Color {
    const fresnelMaterial = material as CharacterFresnelMaterial
    return fresnelMaterial.userData.fresnelTintColor ?? material.color
}

export function setCharacterFresnelTint(material: MeshBasicMaterial, color: Color) {
    const fresnelMaterial = material as CharacterFresnelMaterial
    if (fresnelMaterial.userData.fresnelTintColor) {
        fresnelMaterial.userData.fresnelTintColor.copy(color)
        fresnelMaterial.userData.fresnelShader?.uniforms.fresnelTintColor.value.copy(color)
        return
    }
    material.color.copy(color)
}


// ─── Farb-Hilfsfunktionen ─────────────────────────────────────────────────────
export function withHalfSaturation(color: ColorRepresentation): Color {
    const nextColor = new Color(color)
    nextColor.getHSL(_hslScratch)
    nextColor.setHSL(_hslScratch.h, _hslScratch.s * COLOR_SATURATION_MULTIPLIER, _hslScratch.l)
    return nextColor
}

export function getMeshBasicMaterials(mesh: Mesh | null): MeshBasicMaterial[] {
    if (!mesh) return []
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
    return mats.filter((m): m is MeshBasicMaterial => m instanceof MeshBasicMaterial)
}


// ─── Skin-Flash ───────────────────────────────────────────────────────────────
export function triggerSkinFlash(
    flashRef: { current: { color: Color; remaining: number } | null },
    color: ColorRepresentation,
    duration: number,
) {
    flashRef.current = { color: withHalfSaturation(color), remaining: duration }
}

export function applySkinFlashColor(
    materials: MeshBasicMaterial[],
    color: Color,
    delta: number,
) {
    const alpha = 1 - Math.exp(-delta * SKIN_COLOR_LERP_SPEED)
    for (const material of materials) {
        const nextColor = getCharacterFresnelTint(material).clone().lerp(color, alpha)
        setCharacterFresnelTint(material, nextColor)
    }
}

export function applySkinColorState(
    materials: MeshBasicMaterial[],
    defaultColor: ColorRepresentation,
    collectShieldColor: ColorRepresentation,
    isShieldActive: boolean,
) {
    const color = withHalfSaturation(isShieldActive ? collectShieldColor : defaultColor)
    for (const material of materials) {
        setCharacterFresnelTint(material, color)
    }
}


// ─── Outline-State ────────────────────────────────────────────────────────────
type OutlineStateParams = {
    outline: OutlineHandle | null
    defaultThickness: number
    defaultColor: ColorRepresentation
    defaultStrength: number
    defaultGlow: number
    defaultPulsePeriod: number
    defaultSoftness: number
    defaultAdditive: boolean
    collectThickness: number
    collectShieldColor: ColorRepresentation
    collectStrength: number
    collectGlow: number
    collectPulsePeriod: number
    collectSoftness: number
    collectAdditive: boolean
    isPlaneActive: boolean
    isShieldActive: boolean
}

function applyCollectOutline(outline: OutlineHandle, params: OutlineStateParams) {
    outline.setThickness(params.collectThickness)
    outline.setStrength(params.collectStrength)
    outline.setGlow(params.collectGlow)
    outline.setPulsePeriod(params.collectPulsePeriod)
    outline.setDownSampleRatio(params.collectSoftness)
    outline.setAdditive(params.collectAdditive)
}

export function applyOutlineState(params: OutlineStateParams) {
    const { outline } = params
    if (!outline) return

    if (params.isPlaneActive) {
        applyCollectOutline(outline, params)
        return
    }

    if (params.isShieldActive) {
        applyCollectOutline(outline, params)
        outline.setColor(withHalfSaturation(params.collectShieldColor))
        return
    }

    outline.setThickness(params.defaultThickness)
    outline.setStrength(params.defaultStrength)
    outline.setGlow(params.defaultGlow)
    outline.setPulsePeriod(params.defaultPulsePeriod)
    outline.setDownSampleRatio(params.defaultSoftness)
    outline.setAdditive(params.defaultAdditive)
    outline.setColor(withHalfSaturation(params.defaultColor))
}
